import { useEffect, useState } from "react";

import {
  calculateAdjustedCommissionRate,
  calculateTierCommissions,
  calculateTierPortions,
  calculateTierThresholds,
  getRampPercentageForMonth,
} from "./calculationUtils";
import { CumulativeData, MonthlyEarnings, TierInfo } from "./types";

/**
 * Hook for calculating monthly earnings based on all input parameters
 */
export function useMonthlyEarnings(
  annualQuota: number,
  commissionRate: number,
  baseSalary: number,
  targetCommission: number,
  accelerators: { threshold: number; multiplier: number }[],
  monthlyRampedQuotas: number[],
  guaranteeMonths1to3: number,
  guaranteeMonths4to6: number,
  rampMonths1to3: number,
  rampMonths4to6: number,
  rampMonths7to9: number,
  rampMonths10to12: number,
  monthlyPerformances: number[],
): MonthlyEarnings[] {
  const [monthlyEarnings, setMonthlyEarnings] = useState<MonthlyEarnings[]>([]);

  useEffect(() => {
    if (commissionRate <= 0 || monthlyRampedQuotas.length === 0) return; // Prevent division by zero

    // Calculate monthly earnings with ramp and protection
    const monthlySalary = baseSalary / 12;
    const monthlyTargetCommission = targetCommission / 12;

    // Create basic earnings records without accelerators
    const prelimMonthlyEarnings = monthlyRampedQuotas.map(
      (monthQuota, index) => {
        const month = index + 1;
        const monthPerformance = monthlyPerformances[index] || 0;
        const actualMonthSales = (monthQuota * monthPerformance) / 100;

        return {
          month,
          monthQuota,
          commissionableRevenue: actualMonthSales,
          monthPerformance,
        };
      },
    );

    // Calculate accelerators based on cumulative revenue
    let monthlyEarningsArr = monthlyRampedQuotas.map((monthQuota, index) => {
      const month = index + 1;
      const monthPerformance = monthlyPerformances[index] || 0;
      const actualMonthSales = (monthQuota * monthPerformance) / 100;

      // Get ramp percentage for the current month
      const rampPercentage = getRampPercentageForMonth(
        month,
        rampMonths1to3,
        rampMonths4to6,
        rampMonths7to9,
        rampMonths10to12,
      );

      // Calculate adjusted commission rate based on ramp percentage
      const adjustedCommissionRate = calculateAdjustedCommissionRate(
        commissionRate,
        rampPercentage,
      );

      // Calculate standard commission
      let earnedCommission = (actualMonthSales * adjustedCommissionRate) / 100;
      // Always keep track of what would have been earned at standard rate
      const standardCommission = earnedCommission;

      // Track if accelerator applies
      let isAccelerator = false;
      let tierInfo: TierInfo = {
        normalPortion: 0,
        tierPortions: [],
        tierThresholds: [],
        annualQuotaPercentage: 0,
      };

      // Get or calculate total revenue so far (including current month)
      const previousMonthsRevenue =
        index > 0
          ? prelimMonthlyEarnings
              .slice(0, index)
              .reduce((sum, m) => sum + m.commissionableRevenue, 0)
          : 0;
      const totalRevenueSoFar = previousMonthsRevenue + actualMonthSales;

      // Calculate tier thresholds
      const tierThresholds = calculateTierThresholds(annualQuota, accelerators);

      // Calculate if we're past the quota threshold point for accelerators
      const annualQuotaPercentage = (totalRevenueSoFar / annualQuota) * 100;

      // First accelerator threshold (or 0 if no accelerators)
      const firstAcceleratorThreshold =
        accelerators.length > 0 ? accelerators[0].threshold : 0;

      // Apply accelerators if total revenue exceeds threshold percentage of annual quota
      try {
        // Skip all accelerator calculations if there are no accelerators defined
        if (accelerators.length === 0) {
          // Just use standard commission rate
          earnedCommission = (actualMonthSales * adjustedCommissionRate) / 100;
        } else if (annualQuotaPercentage > firstAcceleratorThreshold) {
          isAccelerator = true;

          // Calculate tier portions
          tierInfo = calculateTierPortions(
            actualMonthSales,
            previousMonthsRevenue,
            totalRevenueSoFar,
            tierThresholds,
            annualQuotaPercentage,
            firstAcceleratorThreshold,
          );

          // Get the multipliers from each accelerator
          const acceleratorMultipliers = accelerators.map(
            (acc) => acc.multiplier,
          );

          // Calculate if we have any revenue portions
          const hasTierPortions =
            tierInfo.normalPortion > 0 ||
            tierInfo.tierPortions.some((portion) => portion > 0);

          if (hasTierPortions) {
            // Calculate commissions for each tier
            const tierCommissions = calculateTierCommissions(
              tierInfo,
              adjustedCommissionRate,
              acceleratorMultipliers,
            );

            // Set the total earned commission
            earnedCommission = tierCommissions.totalCommission;
          }
        }
      } catch {
        // Reset to default values in case of error
        tierInfo = {
          normalPortion: 0,
          tierPortions: [],
          tierThresholds: [],
          annualQuotaPercentage: 0,
        };
        isAccelerator = false;
      }

      const baseCommission = standardCommission;

      let acceleratorAmount = 0;
      if (isAccelerator) {
        acceleratorAmount = earnedCommission - baseCommission;
      }

      // Calculate the guarantee amount if applicable
      let protectionAmount = 0;
      let isProtectionApplied = false;
      let protectionPercentage = 0;
      let commissionPaid = earnedCommission;

      if (month <= 3) {
        protectionPercentage = guaranteeMonths1to3;
        protectionAmount =
          (monthlyTargetCommission * guaranteeMonths1to3) / 100;
      } else if (month <= 6) {
        protectionPercentage = guaranteeMonths4to6;
        protectionAmount =
          (monthlyTargetCommission * guaranteeMonths4to6) / 100;
      }

      // Determine if protection should be applied (earned is less than protection amount)
      if (month <= 6 && protectionAmount > earnedCommission) {
        isProtectionApplied = true;
        commissionPaid = protectionAmount;
      }

      return {
        month,
        salary: monthlySalary,
        quota: monthQuota,
        commissionableRevenue: actualMonthSales,
        rampPercentage,
        baseCommission, // Base commission without accelerators
        earnedCommission, // Total earned commission (base + accelerator)
        protectionAmount: isProtectionApplied ? protectionAmount : 0,
        protectionPercentage: isProtectionApplied ? protectionPercentage : 0,
        isProtectionApplied,
        isAccelerator,
        acceleratorAmount,
        annualQuotaPercentage: Math.round(annualQuotaPercentage),
        previousMonthsRevenue,
        totalRevenueSoFar,
        commissionPaid,
        total: monthlySalary + commissionPaid,
        netValue: actualMonthSales - (monthlySalary + commissionPaid),
        remainingQuota: 0, // Will be set in a second pass
        quotaCompletion: 0, // Will be set in a second pass
        tierInfo, // Debug info for tiers
        rampedQuotaPercentage: 0, // Will be set in a second pass
        currentRampAttainment: 0, // Will be set in a second pass
      };
    });

    // Calculate cumulative quota values
    let cumulativeRevenue = 0;

    // Calculate total ramp-adjusted annual quota
    const totalRampedQuota = monthlyRampedQuotas.reduce(
      (sum, quota) => sum + quota,
      0,
    );

    const processedEarnings = monthlyEarningsArr.map((month, index) => {
      cumulativeRevenue += month.commissionableRevenue;

      // Calculate the cumulative ramped quota up to this month
      const cumulativeRampedQuota = monthlyRampedQuotas
        .slice(0, index + 1)
        .reduce((sum, quota) => sum + quota, 0);

      // Calculate the running percentage against ramp-adjusted annual quota
      const rampedQuotaPercentage =
        totalRampedQuota > 0
          ? Math.round((cumulativeRevenue / totalRampedQuota) * 100)
          : 0;

      // Calculate the running percentage against the cumulative ramped quota
      const currentRampAttainment =
        cumulativeRampedQuota > 0
          ? Math.round((cumulativeRevenue / cumulativeRampedQuota) * 100)
          : 0;

      return {
        ...month,
        rampedQuotaPercentage, // Running % against total ramped quota
        currentRampAttainment, // Running % against cumulative ramped quota so far
        remainingQuota: Math.max(0, annualQuota - cumulativeRevenue),
        quotaCompletion: Math.min(
          100,
          Math.round((cumulativeRevenue / annualQuota) * 100),
        ),
      };
    });

    setMonthlyEarnings(processedEarnings);
  }, [
    annualQuota,
    commissionRate,
    baseSalary,
    targetCommission,
    accelerators, // Now using the accelerators array
    monthlyRampedQuotas,
    guaranteeMonths1to3,
    guaranteeMonths4to6,
    rampMonths1to3,
    rampMonths4to6,
    rampMonths7to9,
    rampMonths10to12,
    monthlyPerformances,
  ]);

  return monthlyEarnings;
}

/**
 * Hook for calculating break-even analysis data
 */
export function useBreakEvenAnalysis(monthlyEarnings: MonthlyEarnings[]) {
  const [cumulativeData, setCumulativeData] = useState<CumulativeData[]>([]);
  const [breakEvenMonth, setBreakEvenMonth] = useState<number | null>(null);

  useEffect(() => {
    if (monthlyEarnings.length > 0) {
      // Calculate cumulative data for break-even analysis
      let cumulativeCost = 0;
      let cumulativeRevenue = 0;
      let foundBreakEven = false;
      let breakEvenMonthFound = null;

      const cumulativeSeriesData = monthlyEarnings.map((month) => {
        // Monthly cost is salary + commission paid
        const monthlyCost = month.salary + month.commissionPaid;
        cumulativeCost += monthlyCost;

        // Monthly revenue is commissionable revenue
        const monthlyRevenue = month.commissionableRevenue;
        cumulativeRevenue += monthlyRevenue;

        // Check for break-even point
        if (!foundBreakEven && cumulativeRevenue >= cumulativeCost) {
          foundBreakEven = true;
          breakEvenMonthFound = month.month;
        }

        return {
          month: month.month,
          monthlyCost,
          monthlyRevenue,
          cumulativeRevenue,
          cumulativeCost,
          netValue: cumulativeRevenue - cumulativeCost,
        };
      });

      setCumulativeData(cumulativeSeriesData);
      setBreakEvenMonth(breakEvenMonthFound);
    }
  }, [monthlyEarnings]);

  return { cumulativeData, breakEvenMonth };
}
